import type { UserDto } from "../api-client";

/**
 * Calendar column ordering.
 *
 * The backend does not sort the users it delivers — it only stamps each user
 * with `columnSortRank`, the position configured for the location that was
 * requested (see the API's calendar-column-order.logic.ts). Ordering lives here
 * so that every view — Hauptkalender, Dauertermin, Arbeitszeiten — arranges its
 * columns identically without each endpoint having to remember to sort.
 *
 * Users without a configured rank sort after the configured ones, by name, so a
 * location whose order has never been arranged looks exactly as it did before
 * the feature existed.
 */

/**
 * The app's definition of "alphabetical" for users: last name, then first name,
 * then id. This is the order an unconfigured location renders in, so anything
 * offering to sort users alphabetically must use it rather than rolling its own
 * — otherwise "reset to alphabetical" produces an order that differs from what
 * an unranked location actually shows.
 */
export const compareUsersByName = (a: UserDto, b: UserDto) => {
  const last = (a.lastName || "").localeCompare(b.lastName || "", "de");
  if (last !== 0) return last;
  const first = (a.firstName || "").localeCompare(b.firstName || "", "de");
  if (first !== 0) return first;
  return (a.id || "").localeCompare(b.id || "");
};

/** Ranked users first (ascending), then the unranked ones by name. */
export const compareByColumnOrder = (a: UserDto, b: UserDto): number => {
  const rankA = a.columnSortRank;
  const rankB = b.columnSortRank;
  const hasA = rankA !== null && rankA !== undefined;
  const hasB = rankB !== null && rankB !== undefined;

  if (hasA && hasB && rankA !== rankB) return (rankA as number) - (rankB as number);
  if (hasA !== hasB) return hasA ? -1 : 1;
  return compareUsersByName(a, b);
};

/** Non-mutating sort of a user list into calendar column order. */
export const sortUsersByColumnOrder = <T extends UserDto>(users: T[]): T[] =>
  users.slice().sort(compareByColumnOrder);
